import type {ChatState, Turn} from '@microsoft/agent-host-protocol'
import {use, useEffect, useSyncExternalStore} from 'react'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- the agents workbench's shared preview provider remains in this Relay-dependent package
import {ContentPreviewContext} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import type {Artifact} from '../../utils/artifacts/artifact-core'
import {selectDefaultArtifact} from '../../utils/artifacts/select-default-artifact'
import {
  claimTurnById,
  getAutoOpenEligibilityVersion,
  getTaskAutoOpenState,
  setActiveAutoOpenTask,
  subscribeToAutoOpenEligibility,
} from '../../utils/artifacts/user-started-turns'
import {AgentsWorkbenchContext} from '../workbench/AgentsWorkbenchContext'
import {buildArtifactPreviewItem} from './use-open-artifact-preview'

const DESKTOP_WORKBENCH_MEDIA_QUERY = '(min-width: 1012px)'
const PREVIEW_INITIAL_VIEW_MODE_LANGUAGES = new Set(['html', 'mermaid'])

function getTurnToolCallIds(turn: Turn): Set<string> {
  return new Set(turn.responseParts.flatMap(part => (part.kind === 'toolCall' ? [part.toolCall.toolCallId] : [])))
}

function getTurnTimestamp(turn: Turn): Date {
  const timestamp = turn.startedAt ? Date.parse(turn.startedAt) : Number.NaN
  return Number.isNaN(timestamp) ? new Date() : new Date(timestamp)
}

export function useAutoOpenSessionArtifact(
  taskId: string,
  chatState: ChatState | undefined,
  artifacts: readonly Artifact[],
  historyIsSettled: boolean,
  artifactWritesSettled = false,
) {
  const contentPreview = use(ContentPreviewContext)
  // A preview provider is not by itself the feature gate: the legacy immersive task
  // route mounts one too, so keying off the provider alone would auto-open there.
  // Only the workbench host publishes this context.
  const isInWorkbench = use(AgentsWorkbenchContext)
  const latestTurn = chatState?.turns.at(-1)
  const updateItem = contentPreview?.updateItem
  const requestAutoOpenArtifact = contentPreview?.requestAutoOpenArtifact
  // A dispatch receipt binds outside React's data flow, so without this the effect
  // would only re-run on the next relay event. A turn that was already complete
  // when its receipt arrived would then never be considered.
  const eligibilityVersion = useSyncExternalStore(
    subscribeToAutoOpenEligibility,
    getAutoOpenEligibilityVersion,
    getAutoOpenEligibilityVersion,
  )

  // Task surfaces are keyed by task id, so a task switch unmounts this hook instead
  // of rerendering it with a new task. Declaring the task on every mount is what
  // retires the outgoing task's submissions, which a cleanup tied to this hook's own
  // lifetime would miss.
  useEffect(() => {
    setActiveAutoOpenTask(taskId)
  }, [taskId])

  useEffect(() => {
    if (!isInWorkbench) return
    const turnState = getTaskAutoOpenState(taskId)
    // Only a turn a dispatch in this page named can open anything. Replayed
    // history and reconnect-restored turns match nothing, and the claim retires
    // the submission so the grant cannot be spent twice.
    if (latestTurn && !turnState.claimedTurnIds.has(latestTurn.id) && claimTurnById(taskId, latestTurn.id)) {
      turnState.claimedTurnIds.add(latestTurn.id)
    }

    if (
      !latestTurn ||
      !turnState.claimedTurnIds.has(latestTurn.id) ||
      turnState.settledTurnIds.has(latestTurn.id) ||
      !historyIsSettled ||
      latestTurn?.state !== 'complete' ||
      !updateItem ||
      !requestAutoOpenArtifact
    ) {
      return
    }

    // A turn that finished below the desktop breakpoint has spent its opportunity.
    // Leaving it eligible would let a later widen, or any other dependency change
    // at desktop width, open an artifact from a turn the user has moved past.
    if (!window.matchMedia(DESKTOP_WORKBENCH_MEDIA_QUERY).matches) {
      turnState.settledTurnIds.add(latestTurn.id)
      return
    }

    const toolCallIds = getTurnToolCallIds(latestTurn)
    const selection = selectDefaultArtifact(artifacts.filter(candidate => toolCallIds.has(candidate.toolCallId)))
    if (selection.status === 'pending') return
    if (selection.status === 'ambiguous') {
      // Nothing that arrives later can break the tie, so this turn opens nothing and
      // is done. Leaving it eligible would let an unrelated artifact update, such as
      // deduplication moving a tool call onto a newer turn, make this turn look
      // unambiguous and reopen the workbench to output the user has moved past.
      turnState.settledTurnIds.add(latestTurn.id)
      return
    }

    const item = buildArtifactPreviewItem(
      selection.artifact,
      getTurnTimestamp(latestTurn),
      PREVIEW_INITIAL_VIEW_MODE_LANGUAGES.has(selection.artifact.language) ? 'preview' : undefined,
      {catalog: artifacts, writesSettled: artifactWritesSettled},
    )
    if (!item) return

    updateItem(item)
    requestAutoOpenArtifact(item.id)
    turnState.settledTurnIds.add(latestTurn.id)
  }, [
    artifacts,
    artifactWritesSettled,
    eligibilityVersion,
    historyIsSettled,
    isInWorkbench,
    latestTurn,
    requestAutoOpenArtifact,
    taskId,
    updateItem,
  ])
}
